import { useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    getNoticeDetails,
    updateNotice,
    publishNotice,
    getNoticeEnums,
    getNoticeAudienceOptions,
    uploadNoticeAttachments,
} from '../../../redux/actions/SocietyPortal/NoticeAction/NoticeAction';
import { getWingsDropdown } from '../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_NOTICES } from '../../../Routing/routes';
import NoticeForm from './NoticeForm';

// <input type="datetime-local"> wants "YYYY-MM-DDTHH:mm" in local time, while
// the API returns an ISO/UTC string. Slicing the raw value would shift the time
// by the timezone offset, so the parts are read off a local Date instead.
const toLocalInput = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const EditNotice = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const {
        updateNoticeLoading,
        noticeDetailsData,
        noticeDetailsLoading,
        noticeEnumsData,
        audienceOptionsData,
        audienceOptionsLoading,
    } = useSelector(state => state.NoticeReducer || {});
    const { wingsDropdown } = useSelector(state => state.WingsAndFlatsReducers || {});
    // One call gives members (with owner/tenant/employee kinds and their flats),
    // flats with occupancy counts, and wings — everything the pickers filter on.
    const members = useMemo(
        () => (Array.isArray(audienceOptionsData?.members) ? audienceOptionsData.members : []),
        [audienceOptionsData]
    );
    const flatOptions = useMemo(
        () => (Array.isArray(audienceOptionsData?.flats) ? audienceOptionsData.flats : []),
        [audienceOptionsData]
    );
    const wings = useMemo(
        () => (Array.isArray(wingsDropdown) ? wingsDropdown : []),
        [wingsDropdown]
    );

    useEffect(() => {
        if (id) dispatch(getNoticeDetails({ id, societyId }));
    }, [dispatch, id, societyId]);
    useEffect(() => {
        if (!noticeEnumsData) dispatch(getNoticeEnums());
    }, [dispatch, noticeEnumsData]);
    useEffect(() => {
        if (societyId) dispatch(getWingsDropdown({ society_id: societyId }));
    }, [dispatch, societyId]);
    useEffect(() => {
        if (societyId) dispatch(getNoticeAudienceOptions({ societyId }));
    }, [dispatch, societyId]);

    const backToList = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_NOTICES);

    const handleSubmit = (payload, files = [], publish = false) => {
        // Files upload after the update lands, then publish (if asked), then leave —
        // chained so a failure at any step stops the ones after it.
        const finish = () => {
            if (publish) {
                dispatch(publishNotice({
                    id,
                    publish_at: payload.publish_at,
                    onSuccess: backToList,
                }));
                return;
            }
            backToList();
        };

        dispatch(updateNotice({
            id,
            ...payload,
            onSuccess: () => {
                if (files.length > 0) {
                    dispatch(uploadNoticeAttachments({ id, files, onSuccess: finish }));
                    return;
                }
                finish();
            },
        }));
    };

    const detail = noticeDetailsData;
    const initial = useMemo(() => (detail ? {
        notice_type: detail.notice_type || 'NOTICE',
        title: detail.title || '',
        summary: detail.summary || '',
        body: detail.body || '',
        category: detail.category || 'GENERAL',
        priority: detail.priority || 'NORMAL',
        audience: detail.audience || 'ALL',
        flat_ids: Array.isArray(detail.flats)
            ? detail.flats.map(f => f.flat_id)
            : (detail.flat_ids || []),
        recipient_user_ids: Array.isArray(detail.recipients)
            ? detail.recipients.map(u => u.user_id)
            : (detail.recipient_user_ids || []),
        wing_ids: Array.isArray(detail.wings)
            ? detail.wings.map(w => w.wing_id)
            : (detail.wing_ids || []),
        publish_at: toLocalInput(detail.publish_at),
        expires_at: toLocalInput(detail.expires_at),
        is_pinned: !!detail.is_pinned,
        requires_acknowledgement: !!detail.requires_acknowledgement,
        send_email: !!detail.send_email,
        send_sms: !!detail.send_sms,
        send_push: !!detail.send_push,
    } : null), [detail]);

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            <div className="tw-max-w-4xl tw-mx-auto">
                <button
                    onClick={backToList}
                    className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-600 hover:tw-text-gray-800 tw-mb-3"
                >
                    <Icon icon="mdi:arrow-left" /> Back to Notices
                </button>
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm">
                    <div className="tw-p-4 tw-border-b tw-border-gray-100">
                        <h1 className="tw-text-base tw-font-bold tw-text-gray-800">
                            Edit Notice {detail?.notice_code ? `— ${detail.notice_code}` : ''}
                        </h1>
                        <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                            Attachments and acknowledgements are managed on the View page.
                        </p>
                    </div>
                    <div className="tw-p-5">
                        {noticeDetailsLoading && !initial ? (
                            <div className="tw-flex tw-justify-center tw-py-12">
                                <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-sky-500" />
                            </div>
                        ) : (
                            <NoticeForm
                                initial={initial}
                                enums={noticeEnumsData}
                                wings={wings}
                                members={members}
                                membersLoading={!!audienceOptionsLoading}
                                flatOptions={flatOptions}
                                enableAttachments
                                existingAttachments={detail?.attachments || []}
                                onSubmit={handleSubmit}
                                onCancel={backToList}
                                submitting={updateNoticeLoading}
                                submitLabel="Save Changes"
                                mode="edit"
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditNotice;
